import type {
  MutationObserverOptions,
  UseQueryOptions,
  DefaultError,
  QueryKey,
  QueryObserverOptions,
} from '@tanstack/vue-query';
import { promiseTimeout } from '@vueuse/core';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { isTransientError } from '@/api/errors';
import type { ApiError } from '@/api/errors';

// This used to be a fork of UseQueryOptions but it got too complicated to maintain - instead, it's
// now a manually maintained type for what we actually pass in. Feel free to update it if you need
// to. There will be a type error below if you try something TanStack Query won't accept
export type QueryOptions = {
  enabled?: MaybeRefOrGetter<QueryObserverOptions['enabled']>;
  refetchInterval?: number | false | (() => number | false);
  staleTime?: number;
  placeholderData?: any;
  retry?: number;
  retryDelay?: number;
};

export type MutationOptions<TData = unknown, TVariables = void> = MutationObserverOptions<
  TData,
  ApiError,
  TVariables,
  null
>;

const backoffMs = (attempt: number) => Math.min(1000 * 2 ** attempt, 4000);

// Retry options gated on the failure being worth another attempt: a non-transient
// failure such as a 403 still fails on the first one, and bounded backoff keeps
// the wait to a few seconds. Call sites use the named policies below; only the
// global default in plugins/vueQuery.ts passes its own count.
export function retryTransient(maxRetries: number) {
  return {
    retry: (failureCount: number, err: Error) => failureCount < maxRetries && isTransientError(err),
    retryDelay: backoffMs,
  };
}

// Reads a page can't render without. Nothing else recovers them: the user is
// looking at a loading screen that turns into an error screen, not at one broken
// panel, so tolerate a longer outage than the global default. A burst of API 5xx
// during a traffic surge left users refreshing by hand.
const PAGE_READ_REQUIREMENT_RETRIES = 3;

// Writes whose endpoint no-ops on repeat. Fewer attempts than a read because the
// user is waiting on the response, and clicking the button again is a retry.
const IDEMPOTENT_WRITE_RETRIES = 2;

export const retryPageReadRequirement = () => retryTransient(PAGE_READ_REQUIREMENT_RETRIES);

export const retryIdempotentWrite = () => retryTransient(IDEMPOTENT_WRITE_RETRIES);

// The same policy for a bare promise, for the legacy Vuex fetches the app shell
// still waits on and which TanStack Query knows nothing about.
export async function withTransientRetry<T>(
  fn: () => Promise<T>,
  maxRetries = PAGE_READ_REQUIREMENT_RETRIES,
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // Sequential by design: each attempt waits on the previous one failing.
      // oxlint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      if (attempt >= maxRetries || !isTransientError(err)) throw err;
      // oxlint-disable-next-line no-await-in-loop
      await promiseTimeout(backoffMs(attempt));
    }
  }
}

// Callers pass keys holding refs (`[SOME_QUERY, toRef(id)]`); `queryFn` receives them
// unwrapped, and TanStack Query's types tell the two apart, so this helper needs one
// type parameter for each. `UnwrapQueryKey` mirrors its `DeepUnwrapRef`, not exported.
// Collapse back to a single parameter if TanStack/query#10584 is reverted upstream.
type UnwrapLeaf =
  | string
  | number
  | boolean
  | symbol
  | bigint
  | null
  | undefined
  | Date
  | ((...args: never[]) => unknown);

type UnwrapQueryKey<T> = T extends UnwrapLeaf
  ? T
  : T extends Ref<infer TValue>
    ? UnwrapQueryKey<TValue>
    : T extends object
      ? { [TProperty in keyof T]: UnwrapQueryKey<T[TProperty]> }
      : T;

export function mergeQueryOptions<TData = unknown, TQueryKey extends QueryKey = QueryKey>(
  userOptions: QueryOptions | undefined,
  baseOptions: UseQueryOptions<TData, ApiError, TData, TData, TQueryKey>,
): UseQueryOptions<TData, ApiError, TData, TData, UnwrapQueryKey<TQueryKey> & QueryKey> {
  const merged: UseQueryOptions<TData, ApiError, TData, TData, TQueryKey> = {
    ...toValue(baseOptions),
    ...toValue(userOptions),
    enabled: () =>
      toValue(toValue(baseOptions)?.enabled) !== false &&
      toValue(toValue(userOptions)?.enabled) !== false,
  };

  // Refs inside a key are unwrapped at runtime, so only that unwrapping is asserted,
  // and only here rather than at every call site. `utils.test.ts` covers it.
  return merged as UseQueryOptions<
    TData,
    ApiError,
    TData,
    TData,
    UnwrapQueryKey<TQueryKey> & QueryKey
  >;
}

export function mergeMutationOptions<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  userOptions: MutationObserverOptions<TData, TError, TVariables, TContext> | undefined,
  baseOptions: MutationObserverOptions<TData, TError, TVariables, TContext>,
): MutationObserverOptions<TData, TError, TVariables, TContext> {
  return {
    ...baseOptions,
    ...userOptions,
    async onSuccess(data, variables, onMutateResult, context) {
      if (baseOptions.onSuccess) {
        await baseOptions.onSuccess(data, variables, onMutateResult, context);
      }
      if (userOptions?.onSuccess) {
        await userOptions.onSuccess(data, variables, onMutateResult, context);
      }
    },
    async onError(error, variables, onMutateResult, context) {
      if (baseOptions.onError) await baseOptions.onError(error, variables, onMutateResult, context);
      if (userOptions?.onError) {
        await userOptions.onError(error, variables, onMutateResult, context);
      }
    },
  };
}

/**
 * A function to pass an arg and `enabled` to a TanStack Query composable when
 * and only when `arg` is defined.
 *
 * Previously, we had a pattern like this:
 *
 * ```
 * const query = useComposable(() => myArg!, {
 *   enabled: () => !!myArg,
 * });
 * ```
 *
 * This meant we had a lot of non-null assertion operators all over the place,
 * and we could forget to pass the `enabled` option. Now we can write this:
 *
 * ```
 * const query = whenNonNull(
 *   () => myArg,
 *   (enabled, myArg) => useComposable(myArg, { enabled }),
 * );
 * ```
 */
export function whenNonNull<TArg, TReturnType>(
  arg: MaybeRefOrGetter<TArg>,
  // the NonNullable is technically a lie, but if `enabled` is respected then
  // it won't be called when `arg` is undefined. allows us to consolidate all
  // the non-null operators we had previously into a single place
  useComposable: (enabled: () => boolean, arg: () => NonNullable<TArg>) => TReturnType,
): TReturnType {
  return useComposable(
    () => !!toValue(arg),
    // We know this isn't null - we've just tested it
    // oxlint-disable-next-line no-non-null-assertion
    () => toValue(arg)!,
  );
}

/**
 * Same as whenNonNull, but with two arguments.
 *
 * Having an array of generics in TypeScript is very complicated / impossible,
 * so we duplicate the function instead.
 */
export function whenBothNonNull<TArg1, TArg2, TReturnType>(
  arg1: MaybeRefOrGetter<TArg1>,
  arg2: MaybeRefOrGetter<TArg2>,
  useComposable: (
    enabled: () => boolean,
    arg1: () => NonNullable<TArg1>,
    arg2: () => NonNullable<TArg2>,
  ) => TReturnType,
): TReturnType {
  return useComposable(
    () => !!toValue(arg1) && !!toValue(arg2),
    // We know this isn't null - we've just tested it
    // oxlint-disable-next-line no-non-null-assertion
    () => toValue(arg1)!,
    // We know this isn't null - we've just tested it
    // oxlint-disable-next-line no-non-null-assertion
    () => toValue(arg2)!,
  );
}
